import { Injectable } from '@angular/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { LawFirmItem } from '../law-firm/law-firm.service';

export const feeTypes = [
  'amount',
  'percentage',
  'unknown',
] as const;

export const feePaymentOccurrences = [
  'start',
  'closed-success',
] as const;

export const feeFeeTypes = [
  'indirect',
  'payee',
] as const;

export type Fee = {
  id: number;
  label: string;
  currency_iso: string | null;
  fee: number;
  min: number | null;
  max: number | null;
  type: typeof feeTypes[number];
  is_net: boolean;
  payment_occurrence: typeof feePaymentOccurrences[number];
  fee_type: typeof feeFeeTypes[number];
  vat_id: number;
};

export const productTypes = [
  'notice',
  'demand_letter',
  'hard_1',
  'hard_2',
  'coface',
] as const;
export type ProductType = typeof productTypes[number];

export type Product = {
  id: number;
  name: string;
  type: ProductType;
  client_country_isos: string[] | null;
  debtor_country_isos: string[] | null;
  is_active: boolean;
  fees: Fee[];
  defaultLawFirms: LawFirmItem[];
};

type ListProductParams = {
  client_country_iso?: string;
  debtor_country_iso?: string;
};

type GetProductResponse = {
  products: Product[];
};

type CreateProductParams = Omit<Product, 'id'> & {
  client_country_isos: null | string[];
  debtor_country_isos: null | string[];
  defaultLawFirms: number[];
};
type UpdateProductParams = Omit<Product, 'defaultLawFirms'> & {
  client_country_isos: null | string[];
  debtor_country_isos: null | string[];
  defaultLawFirms: number[] | LawFirmItem[];
};

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  async getProducts(params?: ListProductParams): Promise<GetProductResponse> {
    const result = await axios.get<GetProductResponse>(
      `${environment.baseUrl}/api/product`,
      {
        params,
      }
    );
    return result.data;
  }

  async activateProduct(product: Product): Promise<void> {
    const url = `${environment.baseUrl}/api/product/${product.id}/activate`;
    await axios.post(url);
  }

  async deactivateProduct(product: Product): Promise<void> {
    const url = `${environment.baseUrl}/api/product/${product.id}/deactivate`;
    await axios.post(url);
  }

  async createProduct(params: CreateProductParams): Promise<Product> {
    const url = `${environment.baseUrl}/api/product`;
    const result = await axios.post<{ product: Product; }>(url, params);
    return result.data.product;
  }

  async updateProduct(product: Product, params: UpdateProductParams): Promise<void> {
    const url = `${environment.baseUrl}/api/product/${product.id}`;
    await axios.post(url, {
      ...params,
      '_method': 'PUT',
    });
  }

  async deleteProduct(product: Product): Promise<void> {
    const url = `${environment.baseUrl}/api/product/${product.id}`;
    await axios.delete(url);
  }
}
